import {FALL_8, SUMMER_2} from '@gaia/colors';
import {
    getControlPointImage,
    getMarkerImg,
    getRedPinImg,
    mapboxImagePixelRatio,
} from '@gaia/utils/gaia/getMapboxImages';
import type {MapboxImage} from '@gaia/utils/mapbox/Mapbox';
import {
    assertControlPointState,
    assertControlPointType,
    assertGaiaColorHex,
    isControlPointImageType,
    isMarkerType,
} from '@gaia/utils/typing';
import {preload} from '@map/Map/usePreloadMapboxImages';
import type {EventData} from 'mapbox-gl';

export async function onStyleImageMissing(e: EventData) {
    const imageName: string = e.id;
    const mapRef: MapboxMap = e.target;

    // control point image name: [start|middle|end].#COLOR
    // marker naming type: [pin|icon|control-point].[|tent|emoji-happy|...].#COLOR

    // step one: identify type
    //  which is either a waypoint marker or a control point

    // step two: if waypoint marker, do that. else if control point, do that.

    let image: MapboxImage;

    const imageType: string = imageName.split('.')[0];

    if (isMarkerType(imageType)) {
        try {
            const [, markerDecoration, markerColor] = imageName.split('.');

            assertGaiaColorHex(markerColor);

            // markerDecoration cannot be undefined if `markerColor` is valid

            image = await getMarkerImg(imageType, markerDecoration, markerColor);
        } catch {
            image = await getRedPinImg();
        }
    } else if (isControlPointImageType(imageType)) {
        // control-point.[start|middle|end].#COLOR
        const [, controlPointType, controlPointColor, controlPointState] = imageName.split('.');

        assertControlPointType(controlPointType);
        assertControlPointState(controlPointState);

        image = await getControlPointImage(controlPointType, controlPointColor, controlPointState);
    } else if (imageName in preload) {
        // map requests an image that we know about, but hasn't been preloaded yet; assume it's
        // just taking a while to load. See: `usePreloadMapboxImages.ts`
        // eslint-disable-next-line no-console
        console.info(`map requested ${imageName} before it was preloaded`);
        return;
    } else {
        if (DEPLOYMENT !== 'production') {
            console.warn('caught style missing image', imageName);
            image = getMissingImageLabel(imageName);
        } else {
            throw new Error(`unexpected image type: ${imageType}`);
        }
    }

    if (!mapRef.hasImage(imageName)) {
        mapRef.addImage(imageName, image, {
            pixelRatio: mapboxImagePixelRatio,
        });
    }
}

function getMissingImageLabel(label: string): ImageData {
    const ctx = document.createElement('canvas').getContext('2d')!;

    const em = 32;
    const padding = em / 6; // padding around the label
    const stroke = padding / 2; // stroke around the label

    ctx.font = `${em}px Inter,system-ui`;

    const {width, actualBoundingBoxAscent, actualBoundingBoxDescent} = ctx.measureText(label);
    const height = actualBoundingBoxDescent + actualBoundingBoxAscent;

    ctx.canvas.width = width + 2 * padding;
    ctx.canvas.height = height + 2 * padding;

    ctx.fillStyle = SUMMER_2;
    ctx.strokeStyle = FALL_8;
    ctx.setLineDash([4]);
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.strokeRect(stroke / 2, stroke / 2, ctx.canvas.width - stroke, ctx.canvas.height - stroke);
    ctx.font = `${em}px Inter,system-ui`;
    ctx.fillStyle = FALL_8;
    ctx.fillText(label, padding, height + padding);

    return ctx.getImageData(0, 0, ctx.canvas.width, ctx.canvas.height);
}
